import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Spacing, Text, MessageBox } from '@reservamos/elements';
import useAdyenGooglePay from 'hooks/strategies/useAdyenGooglePay';
import usePurchase from 'hooks/store/usePurchase';
import CheckList from 'ui/atoms/CheckList';
import WalletButtonContainer from '../shared/WalletButtonContainer';
import './AdyenGooglePayForm.scss';

/**
 * AdyenGooglePayForm component.
 * Render Adyen Google Pay button and payment instructions.
 * @param {Object} props - onPaymentButtonClick.
 * @param {Function} props.onPaymentButtonClick - Callback when Google Pay button is clicked.
 * @returns {JSX.Element} Google Pay payment form with native button.
 */
const AdyenGooglePayForm = ({ onPaymentButtonClick }) => {
  const { t } = useTranslation('payment');
  const {
    isUpdating,
    updatingInsurance,
    updatingServiceLevel,
    updatingCarbonOffset,
    applyingDiscount,
    isUpdatingFlatFare,
  } = usePurchase();

  const { containerRef, isLoading, error, isAvailable } = useAdyenGooglePay({
    onPaymentButtonClick,
  });

  if (error && !isLoading) {
    return (
      <div className="checkout-section">
        <MessageBox type="error">
          <Text>
            {t(
              `google_pay_status.${error}`,
              t('google_pay_status.default_error', 'Google Pay is not available at this time'),
            )}
          </Text>
        </MessageBox>
      </div>
    );
  }

  // Hide the native Google Pay button while the purchase total is being recalculated by any
  // ancillary toggle (insurance, service level / "Boleto flexible", carbon offset, flat fare) or
  // a discount code. The native button bypasses the disabled-button guard the standard checkout
  // button already has in Checkout.js, so it must be gated here explicitly.
  const isUpdatingTotal =
    updatingInsurance ||
    updatingServiceLevel ||
    updatingCarbonOffset ||
    applyingDiscount ||
    isUpdatingFlatFare;
  const showLoading = isLoading || isUpdating || isUpdatingTotal || !isAvailable;

  return (
    <div className="checkout-section googlepay-form">
      {showLoading ? (
        <div className="googlepay-loading">
          <Text>
            {isUpdating
              ? t('google_pay_status.processing_payment', 'Processing payment...')
              : t('google_pay_status.initializing', 'Initializing Google Pay...')}
          </Text>
        </div>
      ) : (
        <Spacing vertical>
          <CheckList
            title={t('label.pay_with_google_pay', 'Pay with Google Pay')}
            labels={[
              t('google_pay_instructions.first', 'Click the Google Pay button'),
              t('google_pay_instructions.second', 'Select your payment method and confirm'),
              t('google_pay_instructions.third', 'Your purchase will be processed automatically'),
            ]}
          />
        </Spacing>
      )}

      <WalletButtonContainer
        ref={containerRef}
        onPaymentButtonClick={onPaymentButtonClick}
        hidden={showLoading}
        label={t('label.pay_with_google_pay', 'Pay with Google Pay')}
        containerClassName="googlepay-button-container"
      />
    </div>
  );
};

AdyenGooglePayForm.propTypes = {
  onPaymentButtonClick: PropTypes.func.isRequired,
};

export default AdyenGooglePayForm;
